/* ==========================================================================
   Barre d'achat fixe de la fiche produit, sur telephone et sur ordinateur.

   PRINCIPE. La barre n'a pas de formulaire et n'appelle jamais le panier. Son
   bouton declenche le bouton d'ajout du formulaire principal. Dawn
   (assets/product-form.js) garde donc la main sur tout le reste : variante
   choisie, quantite, blocage des doubles envois, ouverture du tiroir du panier,
   evenements Shopify, retour du focus.

   UN CLIC = UN AJOUT. Le bouton principal passe en aria-disabled des l'envoi,
   de facon synchrone. Un second clic, sur la barre ou sur le bouton principal,
   est ignore tant que la requete n'est pas terminee.

   FOCUS. Avant de declencher l'envoi, le bouton de la barre recoit le focus :
   product-form.js memorise l'element actif au moment de l'envoi et y rend le
   focus a la fermeture du tiroir du panier.

   AFFICHAGE. Regle relevee sur la page de reference, mesuree pas a pas dans le
   defilement : la barre se montre chaque fois que le bouton d'achat principal
   n'est pas a l'ecran, et pas seulement une fois qu'on l'a depasse. Un bouton
   d'achat reste donc visible en permanence. Deux exceptions. Sur ordinateur, le
   tout premier ecran, ou rien n'apparait tant que le visiteur n'a pas commence a
   descendre (sur telephone, la barre est la des l'arrivee, voir evaluerPosition). Et le bas de page, ou
   elle s'efface avant la carte d'aide ou le footer pour ne pas les recouvrir.

   ERREUR D'AJOUT. Dawn affiche l'erreur sous le bouton principal, hors de
   l'ecran quand on utilise la barre. Le meme message est recopie dans la barre,
   dans une zone role="alert", et disparait quand Dawn masque le sien (nouvel
   essai, par exemple).

   RETOUR DU FOCUS SANS SAUT DE PAGE. A la fermeture du tiroir du panier, Dawn
   rend le focus a l'element memorise au moment de l'envoi. Si c'est le bouton
   de la barre ou le bouton principal, la cible est choisie au moment de la
   fermeture parmi les elements visibles a l'ecran : bouton de la barre, sinon
   bouton principal, sinon icone du panier dans le header collant. Un element
   hors ecran n'est jamais focalise : certains navigateurs mobiles ignorent
   l'option preventScroll et feraient remonter la page jusqu'a lui (0072).
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('yzavo-barre-achat')) return;

  var REQUETE_TELEPHONE = '(max-width: 749px)';

  // Hauteur de defilement en dessous de laquelle la page est consideree comme immobile.
  var DEFILEMENT_MINIMAL = 40;

  function ecrire(element, texte) {
    if (element && element.textContent !== texte) element.textContent = texte;
  }

  class YzavoBarreAchat extends HTMLElement {
    constructor() {
      super();
      this.visible = false;
      this.telephone = window.matchMedia(REQUETE_TELEPHONE);
      this.surPosition = () => this.evaluerPosition();
      this.surChangement = () => this.synchroniser();
      this.surClic = (event) => this.ajouter(event);
      // Apres les ecouteurs d'envoi deja poses (product-form.js memorise l'element actif pendant
      // l'evenement submit) : la tache est differee d'une micro-tache pour passer apres eux.
      this.surEnvoi = () => {
        Promise.resolve().then(() => this.confierRetourFocus());
      };
    }

    connectedCallback() {
      this.idSection = this.dataset.sectionPrincipale;
      this.infos = document.querySelector('product-info[data-section="' + this.idSection + '"]');
      this.bouton = this.querySelector('.yzavo-barre-achat__bouton');
      this.libelle = this.querySelector('.yzavo-barre-achat__libelle');
      this.prixBouton = this.querySelector('.yzavo-barre-achat__prix-bouton');
      this.prixActuel = this.querySelector('.yzavo-barre-achat__actuel');
      this.prixBarre = this.querySelector('.yzavo-barre-achat__barre');
      this.coloris = this.querySelector('.yzavo-barre-achat__coloris');
      this.prix = this.querySelector('.yzavo-barre-achat__prix');
      this.texteErreur = this.querySelector('.yzavo-barre-achat__erreur-texte');
      if (!this.infos || !this.bouton || !this.boutonPrincipal()) return;

      this.panier = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      this.formulaire = document.getElementById('product-form-' + this.idSection);
      if (this.formulaire) this.formulaire.addEventListener('submit', this.surEnvoi);

      this.entete = document.querySelector('.section-header');
      // Limite basse : la carte d'aide si elle est presente, sinon le footer.
      this.limite =
        document.querySelector('[data-yzavo-barre-limite]') ||
        document.querySelector('.shopify-section-group-footer-group');

      // Coloris, prix, disponibilite et chargement : lus sur le formulaire principal a chaque
      // changement (Dawn remplace le selecteur, le prix du bouton, l'attribut disabled, la classe
      // loading...). La barre est hors de cette zone : ses propres ecritures ne relancent rien.
      this.observateur = new MutationObserver(this.surChangement);
      this.observateur.observe(this.infos, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden'],
      });
      this.infos.addEventListener('change', this.surChangement);

      window.addEventListener('scroll', this.surPosition, { passive: true });
      window.addEventListener('resize', this.surPosition, { passive: true });
      this.telephone.addEventListener('change', this.surPosition);
      if ('IntersectionObserver' in window) {
        this.observateurPosition = new IntersectionObserver(this.surPosition);
        this.observateurPosition.observe(this.boutonPrincipal());
        if (this.limite) this.observateurPosition.observe(this.limite);
      }

      this.bouton.addEventListener('click', this.surClic);

      this.synchroniser();
      this.evaluerPosition();
    }

    disconnectedCallback() {
      if (this.observateur) this.observateur.disconnect();
      if (this.observateurPosition) this.observateurPosition.disconnect();
      if (this.infos) this.infos.removeEventListener('change', this.surChangement);
      window.removeEventListener('scroll', this.surPosition);
      window.removeEventListener('resize', this.surPosition);
      this.telephone.removeEventListener('change', this.surPosition);
      if (this.bouton) this.bouton.removeEventListener('click', this.surClic);
      if (this.formulaire) this.formulaire.removeEventListener('submit', this.surEnvoi);
    }

    boutonPrincipal() {
      return document.getElementById('ProductSubmitButton-' + this.idSection);
    }

    // La feuille de style peut masquer la barre sur ordinateur, selon le reglage de la section.
    // On ne renvoie donc jamais son bouton comme cible de focus sans avoir verifie qu'elle est
    // reellement a l'ecran.
    estAffichee() {
      return window.getComputedStyle(this).display !== 'none';
    }

    evaluerPosition() {
      var principal = this.boutonPrincipal();
      if (!principal) {
        this.afficher(false);
        return;
      }

      // Hauteur masquee en haut par le header collant : un bouton passe dessous n'est plus visible.
      var hautMasque = 0;
      if (this.entete) {
        var basEntete = this.entete.getBoundingClientRect().bottom;
        if (basEntete > 0) hautMasque = basEntete;
      }
      var cadre = principal.getBoundingClientRect();
      var boutonAEcran = cadre.bottom > hautMasque && cadre.top < window.innerHeight;
      // Sur telephone, la barre est presente des l'arrivee. Les enregistrements Clarity des visiteurs
      // venus des annonces (0406) montrent que deux sur trois ne descendent jamais : le prix et le
      // bouton d'achat, places sous le premier ecran, ne leur apparaissaient pas. Accord du
      // proprietaire le 19/10/2026. Sur ordinateur, la regle de la reference reste en place.
      var aCommenceADescendre = this.telephone.matches || window.scrollY > DEFILEMENT_MINIMAL;
      var limiteAtteinte = this.limite ? this.limite.getBoundingClientRect().top < window.innerHeight : false;

      this.afficher(aCommenceADescendre && !boutonAEcran && !limiteAtteinte);
    }

    afficher(visible) {
      if (visible === this.visible) return;
      this.visible = visible;
      this.classList.toggle('est-visible', visible);
      if (visible) {
        this.removeAttribute('inert');
        this.removeAttribute('aria-hidden');
      } else {
        // Masquee : hors de la tabulation et de la lecture d'ecran.
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
      }
    }

    // Dawn (product-form.js) memorise pendant l'envoi l'element a qui le tiroir du panier rendra le focus
    // en se fermant (Echap, croix, clic a cote), et le tiroir ne fait qu'appeler sa methode focus(). Si cet element est le bouton de la barre ou le bouton principal,
    // il est remplace par une cible qui choisit, au moment de la fermeture, un element visible.
    // Tout autre element memorise est laisse tel quel.
    confierRetourFocus() {
      var panier = this.panier;
      if (!panier || typeof panier.setActiveElement !== 'function') return;
      var memorise = panier.activeElement;
      if (memorise !== this.bouton && memorise !== this.boutonPrincipal()) return;

      var barre = this;
      panier.setActiveElement({
        focus: function () {
          var cible = barre.choisirRetourFocus();
          // Aucun element visible : le focus n'est pas deplace, la page ne bouge pas.
          if (cible) cible.focus({ preventScroll: true });
        },
      });
    }

    // Cible du retour du focus, toujours visible a l'ecran au moment de la fermeture.
    choisirRetourFocus() {
      if (this.visible && this.estAffichee() && !this.bouton.disabled) return this.bouton;
      var principal = this.boutonPrincipal();
      if (principal && this.estEntierementVisible(principal, true)) return principal;
      var panier = document.getElementById('cart-icon-bubble');
      if (panier && this.estEntierementVisible(panier, false)) return panier;
      return null;
    }

    // Element affiche en entier dans la fenetre. Avec sousEntete, il doit aussi se trouver sous le header
    // collant, qui le recouvrirait sinon.
    estEntierementVisible(element, sousEntete) {
      var cadre = element.getBoundingClientRect();
      if (cadre.width === 0 || cadre.height === 0) return false;
      if (window.getComputedStyle(element).visibility === 'hidden') return false;
      var haut = 0;
      if (sousEntete && this.entete) {
        var basEntete = this.entete.getBoundingClientRect().bottom;
        if (basEntete > 0) haut = basEntete;
      }
      return cadre.top >= haut && cadre.bottom <= window.innerHeight;
    }

    synchroniser() {
      var principal = this.boutonPrincipal();
      if (!principal) return;

      // Erreur d'ajout : meme message que la zone d'erreur de Dawn, affiche tant qu'elle l'est.
      var zoneErreur = this.infos.querySelector('.product-form__error-message-wrapper');
      var messageErreur = '';
      if (zoneErreur && !zoneErreur.hasAttribute('hidden')) {
        var texteDawn = zoneErreur.querySelector('.product-form__error-message');
        messageErreur = texteDawn ? texteDawn.textContent.trim() : '';
      }
      this.classList.toggle('est-en-erreur', messageErreur !== '');
      ecrire(this.texteErreur, messageErreur);

      var valeurs = Array.from(
        this.infos.querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select')
      ).map(function (champ) {
        return champ.value;
      });
      if (valeurs.length) ecrire(this.coloris, valeurs.join(' / '));

      var prixBouton = document.getElementById('Prix-Bouton-' + this.idSection);
      var prix = prixBouton ? prixBouton.textContent.replace('—', '').trim() : '';
      if (prix) ecrire(this.prix, prix);

      var indisponible = principal.hasAttribute('disabled');
      var enCours = principal.classList.contains('loading');
      var bloque = indisponible || principal.getAttribute('aria-disabled') === 'true';

      if (indisponible) {
        // Meme texte que le bouton principal (« Épuisé », « Indisponible »), sans prix.
        var textePrincipal = principal.querySelector('span');
        ecrire(this.libelle, textePrincipal ? textePrincipal.textContent.trim() : '');
        if (this.prixBouton) this.prixBouton.hidden = true;
      } else {
        // Le prix barre est celui de l'offre cochee (lot, 2 ou 3 exemplaires) quand il y en a une,
        // sinon celui du bloc de prix de la colonne d'achat : le bouton affiche deja le prix de
        // l'offre, le barre doit lui correspondre.
        var offre = this.infos.querySelector('.yzavo-offre:has(.yzavo-offre__radio:checked)');
        var barre = (offre && offre.querySelector('.yzavo-offre__prix-barre')) ||
          this.infos.querySelector('.price__sale .price-item--regular, .price--on-sale s');
        ecrire(this.libelle, this.dataset.libelle);
        if (this.prixBouton) this.prixBouton.hidden = false;
        ecrire(this.prixActuel, this.prix.textContent);
        ecrire(this.prixBarre, barre ? barre.textContent.trim() : '');
      }

      // Indisponible : bouton reellement desactive. En cours d'ajout : seulement aria-disabled, pour
      // que le bouton garde le focus qui lui sera rendu a la fermeture du tiroir du panier.
      this.bouton.disabled = indisponible;
      this.bouton.setAttribute('aria-disabled', bloque ? 'true' : 'false');
      this.bouton.classList.toggle('est-en-cours', enCours);
      this.bouton.setAttribute('aria-busy', enCours ? 'true' : 'false');
    }

    ajouter(event) {
      var principal = this.boutonPrincipal();
      if (!principal || principal.hasAttribute('disabled') || principal.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }
      if (document.activeElement !== this.bouton) this.bouton.focus({ preventScroll: true });
      principal.click();
    }
  }

  customElements.define('yzavo-barre-achat', YzavoBarreAchat);
})();